import { supabase } from '../lib/supabase';

/**
 * Compliance Dashboard Service
 * Read-only queries for the 8 compliance summary cards.
 * All functions accept companyId and optional siteIds array.
 * Site managers pass only their assigned site IDs; admins pass all company site IDs.
 */
export const complianceDashboardService = {
  /**
   * Count worker certifications expiring in the next 30 days.
   */
  async getExpiringCertifications(companyId, siteIds = null) {
    try {
      const today = new Date();
      const in30Days = new Date();
      in30Days?.setDate(today?.getDate() + 30);

      const todayStr = today?.toISOString()?.split('T')?.[0];
      const in30DaysStr = in30Days?.toISOString()?.split('T')?.[0];

      let query = supabase
        ?.from('worker_certifications')
        ?.select('id', { count: 'exact', head: true })
        ?.eq('company_id', companyId)
        ?.gte('expiry_date', todayStr)
        ?.lte('expiry_date', in30DaysStr);

      const { count, error } = await query;
      if (error) throw error;
      return { count: count || 0, error: null };
    } catch (error) {
      return { count: 0, error: { message: error?.message } };
    }
  },

  /**
   * Count worker certifications that have already expired.
   */
  async getExpiredCertifications(companyId, siteIds = null) {
    try {
      const today = new Date();
      const todayStr = today?.toISOString()?.split('T')?.[0];

      const { count, error } = await supabase
        ?.from('worker_certifications')
        ?.select('id', { count: 'exact', head: true })
        ?.eq('company_id', companyId)
        ?.lt('expiry_date', todayStr);

      if (error) throw error;
      return { count: count || 0, error: null };
    } catch (error) {
      return { count: 0, error: { message: error?.message } };
    }
  },

  /**
   * Count open incidents scoped by company and optionally by site IDs.
   */
  async getOpenIncidents(companyId, siteIds = null) {
    try {
      let query = supabase
        ?.from('incidents')
        ?.select('id', { count: 'exact', head: true })
        ?.eq('company_id', companyId)
        ?.eq('status', 'open')
        // NSherjFh PR 5: compliance Open-incidents count excludes soft-deleted.
        ?.is('deleted_at', null);

      if (siteIds?.length) {
        query = query?.in('site_id', siteIds);
      }

      const { count, error } = await query;
      if (error) throw error;
      return { count: count || 0, error: null };
    } catch (error) {
      return { count: 0, error: { message: error?.message } };
    }
  },

  /**
   * Count incidents currently under investigation.
   */
  async getInvestigatingIncidents(companyId, siteIds = null) {
    try {
      let query = supabase
        ?.from('incidents')
        ?.select('id', { count: 'exact', head: true })
        ?.eq('company_id', companyId)
        ?.eq('status', 'investigating')
        // NSherjFh PR 5: compliance Investigating count excludes soft-deleted.
        ?.is('deleted_at', null);

      if (siteIds?.length) {
        query = query?.in('site_id', siteIds);
      }

      const { count, error } = await query;
      if (error) throw error;
      return { count: count || 0, error: null };
    } catch (error) {
      return { count: 0, error: { message: error?.message } };
    }
  },

  /**
   * Count RAMS assignments with no matching acknowledgement.
   * Filters by company via rams_documents.company_id.
   */
  async getUnacknowledgedRams(companyId, siteIds = null) {
    try {
      // Get all assignment IDs for this company
      let docsQuery = supabase
        ?.from('rams_documents')
        ?.select('id')
        ?.eq('company_id', companyId)
        // NSherjFh PR 3: compliance roll-up excludes soft-deleted H&S docs.
        ?.is('deleted_at', null);

      if (siteIds?.length) {
        docsQuery = docsQuery?.in('site_id', siteIds);
      }

      const { data: docs, error: docsError } = await docsQuery;
      if (docsError) throw docsError;

      if (!docs?.length) return { count: 0, error: null };

      const docIds = docs?.map(d => d?.id);

      // Get all assignments for those docs
      const { data: assignments, error: assignError } = await supabase
        ?.from('rams_assignments')
        ?.select('id, rams_document_id, worker_user_id')
        ?.in('rams_document_id', docIds);

      if (assignError) throw assignError;
      if (!assignments?.length) return { count: 0, error: null };

      // Get all acknowledgements for those docs
      const { data: acks, error: acksError } = await supabase
        ?.from('rams_acknowledgements')
        ?.select('rams_document_id, worker_user_id')
        ?.in('rams_document_id', docIds);

      if (acksError) throw acksError;

      // Build a set of acknowledged pairs
      const ackedSet = new Set(
        (acks || [])?.map(a => `${a?.rams_document_id}:${a?.worker_user_id}`)
      );

      const unacknowledged = assignments?.filter(
        a => !ackedSet?.has(`${a?.rams_document_id}:${a?.worker_user_id}`)
      );

      return { count: unacknowledged?.length || 0, error: null };
    } catch (error) {
      return { count: 0, error: { message: error?.message } };
    }
  },

  /**
   * Count site induction assignments with no matching completion.
   */
  async getPendingInductions(companyId, siteIds = null) {
    try {
      // Get all induction IDs for this company
      let inductionsQuery = supabase
        ?.from('site_inductions')
        ?.select('id')
        ?.eq('company_id', companyId)
        // NSherjFh PR 9: compliance roll-up excludes soft-deleted inductions.
        // Children (assignments + completions) cascade via this parent
        // filter — no extra filter needed on those reads.
        ?.is('deleted_at', null);

      if (siteIds?.length) {
        inductionsQuery = inductionsQuery?.in('site_id', siteIds);
      }

      const { data: inductions, error: inductionsError } = await inductionsQuery;
      if (inductionsError) throw inductionsError;

      if (!inductions?.length) return { count: 0, error: null };

      const inductionIds = inductions?.map(i => i?.id);

      // Get all assignments
      const { data: assignments, error: assignError } = await supabase
        ?.from('site_induction_assignments')
        ?.select('id, induction_id, worker_user_id')
        ?.in('induction_id', inductionIds);

      if (assignError) throw assignError;
      if (!assignments?.length) return { count: 0, error: null };

      // Get all completions
      const { data: completions, error: completionsError } = await supabase
        ?.from('site_induction_completions')
        ?.select('induction_id, worker_user_id')
        ?.in('induction_id', inductionIds);

      if (completionsError) throw completionsError;

      const completedSet = new Set(
        (completions || [])?.map(c => `${c?.induction_id}:${c?.worker_user_id}`)
      );

      const pending = assignments?.filter(
        a => !completedSet?.has(`${a?.induction_id}:${a?.worker_user_id}`)
      );

      return { count: pending?.length || 0, error: null };
    } catch (error) {
      return { count: 0, error: { message: error?.message } };
    }
  },

  /**
   * Count toolbox talk assignments with no matching confirmation.
   */
  async getUnconfirmedTalks(companyId, siteIds = null) {
    try {
      // Get all talk IDs for this company
      let talksQuery = supabase
        ?.from('toolbox_talks')
        ?.select('id')
        ?.eq('company_id', companyId)
        // NSherjFh PR 10: compliance roll-up excludes soft-deleted
        // toolbox talks. Children (assignments + confirmations) cascade
        // via this parent filter — no extra filter needed on those.
        ?.is('deleted_at', null);

      if (siteIds?.length) {
        talksQuery = talksQuery?.in('site_id', siteIds);
      }

      const { data: talks, error: talksError } = await talksQuery;
      if (talksError) throw talksError;

      if (!talks?.length) return { count: 0, error: null };

      const talkIds = talks?.map(t => t?.id);

      // Get all assignments
      const { data: assignments, error: assignError } = await supabase
        ?.from('toolbox_talk_assignments')
        ?.select('id, talk_id, worker_user_id')
        ?.in('talk_id', talkIds);

      if (assignError) throw assignError;
      if (!assignments?.length) return { count: 0, error: null };

      // Get all confirmations
      const { data: confirmations, error: confirmError } = await supabase
        ?.from('toolbox_talk_confirmations')
        ?.select('talk_id, worker_user_id')
        ?.in('talk_id', talkIds);

      if (confirmError) throw confirmError;

      const confirmedSet = new Set(
        (confirmations || [])?.map(c => `${c?.talk_id}:${c?.worker_user_id}`)
      );

      const unconfirmed = assignments?.filter(
        a => !confirmedSet?.has(`${a?.talk_id}:${a?.worker_user_id}`)
      );

      return { count: unconfirmed?.length || 0, error: null };
    } catch (error) {
      return { count: 0, error: { message: error?.message } };
    }
  },

  /**
   * Count daily tasks where is_completed = false, scoped by site IDs.
   */
  async getIncompleteTasks(companyId, siteIds = null) {
    try {
      let query = supabase
        ?.from('daily_tasks')
        ?.select('id', { count: 'exact', head: true })
        ?.eq('is_completed', false);

      if (siteIds?.length) {
        query = query?.in('site_id', siteIds);
      } else {
        // Admin with no specific sites — scope by company_id if column exists
        query = query?.eq('company_id', companyId);
      }

      const { count, error } = await query;
      if (error) throw error;
      return { count: count || 0, error: null };
    } catch (error) {
      return { count: 0, error: { message: error?.message } };
    }
  },

  /**
   * Count open near misses scoped by company.
   */
  async getOpenNearMisses(companyId) {
    try {
      const { count, error } = await supabase
        ?.from('near_misses')
        ?.select('id', { count: 'exact', head: true })
        ?.eq('company_id', companyId)
        ?.eq('status', 'Open');

      if (error) throw error;
      return { count: count || 0, error: null };
    } catch (error) {
      return { count: 0, error: { message: error?.message } };
    }
  },
};
